import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import {
  getSubAdmins,
  deleteSubAdmin,
  restoreSubAdmin,
} from "../api/subAdminApi";
import { confirmDialog, showError } from "../utils/alert";
import Pagination, { DEFAULT_PER_PAGE } from "../components/Pagination";

export default function SubAdminsList() {
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(DEFAULT_PER_PAGE);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const loadSubAdmins = async () => {
    setLoading(true);
    try {
      const res = await getSubAdmins({
        page,
        per_page: perPage,
        deleted: showDeleted ? 1 : 0,
      });
      setSubAdmins(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      showError("Failed to load sub admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubAdmins();
  }, [page, perPage, showDeleted]);

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({ text: "Delete this sub admin?" });
    if (!confirmed) return;
    try {
      await deleteSubAdmin(id);
      loadSubAdmins();
    } catch (err) {
      showError("Failed to delete sub admin");
    }
  };

  const handleRestore = async (id) => {
    const confirmed = await confirmDialog({ text: "Restore this sub admin?" });
    if (!confirmed) return;
    try {
      await restoreSubAdmin(id);
      loadSubAdmins();
    } catch (err) {
      showError("Failed to restore sub admin");
    }
  };

  return (
    <AdminLayout title="Sub Admins">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">
              {showDeleted ? "Deleted Sub Admin List" : "Sub Admin List"}
            </h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn ${showDeleted ? "btn-outline-secondary" : "btn-outline-danger"}`}
                onClick={() => {
                  setPage(1);
                  setShowDeleted((prev) => !prev);
                }}
                title={showDeleted ? "Back to active sub admins" : "Show deleted sub admins"}
              >
                {showDeleted ? (
                  <>
                    <i className="bi bi-arrow-left me-1" />
                    Back
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-1" />
                    Trash
                  </>
                )}
              </button>
              {!showDeleted && (
                <button
                  className="btn skedq-accent-btn"
                  onClick={() => navigate("/sub-admins/new")}
                >
                  Add New Sub Admin
                </button>
              )}
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  {showDeleted && <th>Deleted At</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={showDeleted ? "6" : "5"} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && subAdmins.length === 0 && (
                  <tr>
                    <td colSpan={showDeleted ? "6" : "5"} className="text-center text-muted">
                      {showDeleted ? "No deleted sub admins found." : "No sub admins found."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  subAdmins.map((a, index) => (
                    <tr key={a.id}>
                      <td>{(page - 1) * perPage + index + 1}</td>
                      <td>{a.name}</td>
                      <td>{a.email}</td>
                      <td>{a.is_verified ? "Active" : "Inactive"}</td>
                      {showDeleted ? (
                        <>
                          <td>{a.deleted_at ? new Date(a.deleted_at).toLocaleString() : "-"}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleRestore(a.id)}
                            >
                              Restore
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => navigate(`/sub-admins/edit/${a.id}`)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(a.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </div>
      </div>
    </AdminLayout>
  );
}

